import { useCallback, useEffect, useMemo, useState } from "react";
import StatusBadge from "../../intelligence/StatusBadge";
import {
  cacheAdminImageHealth,
  cacheAdminImageRecord,
  getAdminImageCache,
  getAdminImageCacheDetail,
  patchAdminImageCache,
} from "../../../../services/midpointApi";
import { resolvePublicImageUrl } from "../../../../utils/resolvePublicImageUrl";

function cacheTone(status) {
  switch (status) {
    case "CACHED":
      return "success";
    case "FAILED":
    case "SOURCE_BROKEN":
    case "UNSAFE_URL":
      return "danger";
    case "SKIPPED":
      return "neutral";
    case "NEEDS_REFRESH":
      return "warning";
    case "TOO_LARGE":
    case "UNSUPPORTED_TYPE":
      return "medium";
    default:
      return "neutral";
  }
}

const ImageCachePage = ({ onBack, onOpenImageHealth, onOpenSourceQuality }) => {
  const [sources, setSources] = useState([]);
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [caching, setCaching] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getAdminImageCache();
      setSources(data.sources || []);
      setStorage(data.storage || null);
    } catch (err) {
      setError(err.message || "Unable to load image cache.");
      setSources([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const summary = useMemo(() => {
    const totals = sources.reduce(
      (accumulator, source) => ({
        total: accumulator.total + (source.totalImages || 0),
        cached: accumulator.cached + (source.cachedImages || 0),
        notCached: accumulator.notCached + (source.notCachedImages || 0),
        failed: accumulator.failed + (source.failedCacheImages || 0),
        skipped: accumulator.skipped + (source.skippedCacheImages || 0),
      }),
      { total: 0, cached: 0, notCached: 0, failed: 0, skipped: 0 }
    );

    const coverage =
      totals.total > 0 ? Math.round((totals.cached / totals.total) * 100) : null;

    return { ...totals, coverage, sourceCount: sources.length };
  }, [sources]);

  const openDetail = async (sourceKey) => {
    setSelectedKey(sourceKey);
    setDetailLoading(true);
    setDetail(null);

    try {
      const data = await getAdminImageCacheDetail(sourceKey, { limit: 80 });
      setDetail(data);
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCacheAll = async () => {
    try {
      setCaching(true);
      await cacheAdminImageHealth({ limit: 200, onlyHealthy: true });
      await loadList();
      setToast({ message: "Image cache job started for all sources.", tone: "success" });
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setCaching(false);
    }
  };

  const handleCacheSource = async (sourceKey, retryFailed = false) => {
    try {
      setCaching(true);
      await cacheAdminImageHealth({
        sourceKey,
        limit: 200,
        onlyHealthy: !retryFailed,
        retryFailed,
      });
      await loadList();
      if (selectedKey === sourceKey) {
        await openDetail(sourceKey);
      }
      setToast({ message: retryFailed ? "Retrying failed cache." : "Source images cached.", tone: "success" });
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setCaching(false);
    }
  };

  const handleCacheImage = async (imageId) => {
    try {
      setCaching(true);
      await cacheAdminImageRecord(imageId, { force: true });
      if (selectedKey) {
        await openDetail(selectedKey);
      }
      await loadList();
      setToast({ message: "Image cached.", tone: "success" });
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setCaching(false);
    }
  };

  const handleSkipImage = async (imageId) => {
    try {
      await patchAdminImageCache(imageId, { markSkipped: true });
      if (selectedKey) {
        await openDetail(selectedKey);
      }
      await loadList();
      setToast({ message: "Image marked skipped.", tone: "success" });
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    }
  };

  const previewUrl = (image) => {
    if (image.cacheStatus === "CACHED" && image.cachedImageUrl) {
      return resolvePublicImageUrl(image.cachedImageUrl);
    }

    if (image.healthStatus === "BROKEN" || image.healthStatus === "INVALID_URL") {
      return null;
    }

    return image.imageUrl;
  };

  return (
    <div className="admin-page image-health-page source-quality-page">
      <header className="admin-page__header">
        <div>
          <button type="button" className="admin-back-link" onClick={onBack}>
            ← Back to map
          </button>
          <p className="eyebrow">Administration · Real Estate</p>
          <h1>Image Cache</h1>
          <p className="admin-page__lede">
            Proxy and cache broker listing images for stable Midpoint-served URLs. Preserves source
            attribution while reducing hotlink failures.
          </p>
        </div>
        <div className="admin-page__actions">
          <button type="button" onClick={handleCacheAll} disabled={caching}>
            {caching ? "Caching…" : "Cache all"}
          </button>
        </div>
      </header>

      {toast ? (
        <p className={`admin-toast admin-toast--${toast.tone}`} role="status">
          {toast.message}
        </p>
      ) : null}

      <section className="source-quality-summary">
        <article className="panel-card">
          <p className="eyebrow">Images tracked</p>
          <strong className="source-quality-summary__value">{summary.total}</strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Cached</p>
          <strong className="source-quality-summary__value">{summary.cached}</strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Not cached</p>
          <strong className="source-quality-summary__value">{summary.notCached}</strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Failed</p>
          <strong className="source-quality-summary__value">{summary.failed}</strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Cache coverage</p>
          <strong className="source-quality-summary__value">
            {summary.coverage != null ? `${summary.coverage}%` : "—"}
          </strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Storage</p>
          <strong className="source-quality-summary__value">
            {storage?.driver || "—"}
          </strong>
          <p className="muted">{storage?.publicBaseUrl || storage?.publicBasePath || "—"}</p>
        </article>
      </section>

      {loading ? <p className="intel-meta">Loading image cache…</p> : null}
      {error ? <p className="intel-error">{error}</p> : null}

      {!loading && !error ? (
        <div className="source-quality-table-wrap">
          <table className="source-quality-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Total</th>
                <th>Cached</th>
                <th>Not cached</th>
                <th>Failed</th>
                <th>Skipped</th>
                <th>Coverage</th>
                <th>Last cached</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((source) => (
                <tr key={source.sourceKey}>
                  <td>
                    <strong>{source.displayName}</strong>
                    <span className="muted">{source.sourceKey}</span>
                  </td>
                  <td>{source.totalImages}</td>
                  <td>{source.cachedImages}</td>
                  <td>{source.notCachedImages}</td>
                  <td>{source.failedCacheImages}</td>
                  <td>{source.skippedCacheImages ?? 0}</td>
                  <td>{source.cacheCoverageScore ?? "—"}%</td>
                  <td>
                    {source.lastCachedAt
                      ? new Date(source.lastCachedAt).toLocaleString()
                      : "—"}
                  </td>
                  <td className="source-quality-table__actions">
                    <button type="button" onClick={() => openDetail(source.sourceKey)}>
                      View images
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCacheSource(source.sourceKey)}
                      disabled={caching}
                    >
                      Cache now
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCacheSource(source.sourceKey, true)}
                      disabled={caching}
                    >
                      Retry failed
                    </button>
                    {onOpenImageHealth ? (
                      <button type="button" onClick={() => onOpenImageHealth()}>
                        Image health
                      </button>
                    ) : null}
                    {onOpenSourceQuality ? (
                      <button type="button" onClick={() => onOpenSourceQuality()}>
                        Source quality
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {selectedKey ? (
        <aside className="source-quality-drawer image-health-drawer" aria-label="Source image cache">
          <div className="source-quality-drawer__header">
            <div>
              <p className="eyebrow">Image cache</p>
              <h2>{detail?.displayName || selectedKey}</h2>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedKey(null);
                setDetail(null);
              }}
            >
              Close
            </button>
          </div>

          {detailLoading ? <p className="intel-meta">Loading images…</p> : null}

          {!detailLoading && detail ? (
            <div className="image-health-drawer__body">
              <div className="image-health-drawer__metrics">
                <StatusBadge label={`${detail.metrics.cachedImages} cached`} tone="success" />
                <StatusBadge label={`${detail.metrics.notCachedImages} not cached`} tone="neutral" />
                <StatusBadge label={`${detail.metrics.failedCacheImages} failed`} tone="danger" />
                <StatusBadge
                  label={`${detail.metrics.cacheCoverageScore ?? "—"}% coverage`}
                  tone="medium"
                />
              </div>

              <ul className="image-health-drawer__list">
                {(detail.images || []).map((image) => {
                  const thumb = previewUrl(image);

                  return (
                    <li key={image.imageId} className="image-health-drawer__item">
                      <div className="image-health-drawer__thumb">
                        {thumb ? (
                          <img src={thumb} alt="" loading="lazy" />
                        ) : (
                          <div className="property-image-placeholder">
                            <span>Unavailable</span>
                          </div>
                        )}
                      </div>
                      <div className="image-health-drawer__meta">
                        <StatusBadge label={image.cacheStatus || "NOT_CACHED"} tone={cacheTone(image.cacheStatus)} />
                        <StatusBadge label={image.healthStatus} tone={cacheTone(image.healthStatus)} />
                        <p className="muted">
                          {image.cacheContentType || "—"}
                          {image.cacheContentLength
                            ? ` · ${Math.round(image.cacheContentLength / 1024)} KB`
                            : ""}
                          {image.cacheFailureReason ? ` · ${image.cacheFailureReason}` : ""}
                        </p>
                        <p className="muted">{image.sourceListingTitle || "Listing"}</p>
                        {image.cachedImageUrl ? (
                          <p className="image-health-drawer__url">Cached: {image.cachedImageUrl}</p>
                        ) : null}
                        <p className="image-health-drawer__url">Original: {image.imageUrl}</p>
                        <div className="source-quality-table__actions">
                          <button type="button" onClick={() => handleCacheImage(image.imageId)} disabled={caching}>
                            {image.cacheStatus === "CACHED" ? "Re-cache" : "Cache"}
                          </button>
                          <button type="button" onClick={() => handleSkipImage(image.imageId)}>
                            Mark skipped
                          </button>
                          {image.imageUrl ? (
                            <a href={image.imageUrl} target="_blank" rel="noreferrer">
                              Open original
                            </a>
                          ) : null}
                          {image.cachedImageUrl ? (
                            <a
                              href={resolvePublicImageUrl(image.cachedImageUrl)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open cached
                            </a>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </aside>
      ) : null}
    </div>
  );
};

export default ImageCachePage;
